import { useEffect, useMemo, useState } from "react"
import { Check } from "lucide-react"

import allInIconUrl from "@/assets/icons/gifts-all-it.png"

import { Sheet, SheetContent } from "@/components/ui/sheet"
import { useI18n } from "@/shared/i18n"
import { cn } from "@/shared/lib/utils"
import { formatTonFromNano } from "@/shared/lib/pvpRouletteMath"
import LargeButton from "@/shared/ui/LargeButton"
import TonIcon from "@/assets/icons/ton-icon.svg?react"
import {
  filterBettableGifts,
  INVENTORY_GIFTS_PARAMS,
  useUserGifts,
} from "@/entities/gift"
import type { UserGift } from "@/shared/api/types"

import { BetSheetHeader } from "./BetSheetHeader"

interface GiftPickerSheetProps {
  roomId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Fires N parallel `placeBet` calls (one per selected gift). */
  onConfirm?: (giftIds: string[]) => void | Promise<void>
}

function getDisplayName(gift: UserGift): string {
  return gift.collection || gift.slug
}

export function GiftPickerSheet({
  roomId,
  open,
  onOpenChange,
  onConfirm,
}: GiftPickerSheetProps) {
  const { t } = useI18n()

  const { data } = useUserGifts(INVENTORY_GIFTS_PARAMS)
  const gifts = useMemo(
    () => filterBettableGifts(data?.items ?? []),
    [data]
  )

  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set())
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    if (!open) {
      setSelectedIds(new Set())
      setIsSubmitting(false)
    }
  }, [open])

  // Prune selection when a gift disappears from the cache (e.g. went HELD via WS).
  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev
      const giftIds = new Set(gifts.map((g) => g.id))
      const next = new Set<string>()
      for (const id of prev) if (giftIds.has(id)) next.add(id)
      return next.size === prev.size ? prev : next
    })
  }, [gifts])

  const totalNano = useMemo(
    () => gifts.reduce((sum, g) => sum + (g.assessed_price_nano ?? 0), 0),
    [gifts]
  )
  const selectedNano = useMemo(() => {
    let sum = 0
    for (const g of gifts) if (selectedIds.has(g.id)) sum += g.assessed_price_nano ?? 0
    return sum
  }, [gifts, selectedIds])

  const allSelected = gifts.length > 0 && selectedIds.size === gifts.length
  const someSelected = selectedIds.size > 0

  const toggleOne = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleAll = () => {
    setSelectedIds(allSelected ? new Set() : new Set(gifts.map((g) => g.id)))
  }

  const handleConfirm = async () => {
    if (!someSelected || isSubmitting) return
    if (!onConfirm) return
    setIsSubmitting(true)
    try {
      await onConfirm(Array.from(selectedIds))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="overflow-hidden rounded-t-[24px] border-none p-0"
        closeClassName="top-4 right-2"
      >
        <div className="flex max-h-[90dvh] w-full flex-col bg-sheet">
          <BetSheetHeader
            roomId={roomId}
            sectorNowPercent={0}
            sectorAfterPercent={0}
          />

          {/* "Your gifts" section label */}
          <div className="flex items-center px-4 pt-2">
            <span className="text-[18px] font-semibold leading-[22px] tracking-[-0.5px] text-white">
              {t("giftPicker.yourGifts")}
            </span>
          </div>

          {/* Grid */}
          <div className="-webkit-overflow-scrolling-touch flex-1 overflow-y-auto px-4 pb-3 pt-3">
            <div className="grid grid-cols-5 gap-x-5 gap-y-4">
              <SelectAllCard
                label={t("giftPicker.allIn")}
                totalNano={totalNano}
                allSelected={allSelected}
                disabled={gifts.length === 0 || isSubmitting}
                onClick={toggleAll}
              />
              {gifts.map((gift) => (
                <GiftPickerCard
                  key={gift.id}
                  gift={gift}
                  selected={selectedIds.has(gift.id)}
                  disabled={isSubmitting}
                  onClick={() => toggleOne(gift.id)}
                />
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="px-4 pb-[calc(1rem+var(--app-safe-bottom))] pt-2">
            <LargeButton
              variant="green"
              disabled={!someSelected || isSubmitting}
              isLoading={isSubmitting}
              onClick={handleConfirm}
            >
              {someSelected ? (
                <>
                  <span>
                    {t("giftPicker.betCta", {
                      amount: formatTonFromNano(selectedNano),
                    })}
                  </span>
                  <div className="relative flex size-[19px] shrink-0 items-center justify-center rounded-full bg-black">
                    <TonIcon
                      className="absolute left-1/2 top-0 size-5 -translate-x-1/2 -translate-y-px text-white drop-shadow-[0_-1px_0_rgb(0,0,0)]"
                    />
                  </div>
                </>
              ) : (
                t("giftPicker.selectGiftsCta")
              )}
            </LargeButton>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  )
}

// ── Cards ────────────────────────────────────────────────────────────────────

function SelectAllCard({
  label,
  totalNano,
  allSelected,
  disabled,
  onClick,
}: {
  label: string
  totalNano: number
  allSelected: boolean
  disabled: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "flex w-full flex-col items-center gap-1 focus:outline-none",
        disabled ? "cursor-not-allowed opacity-40" : "active:scale-[0.97]"
      )}
    >
      <div className="relative w-full pb-2.5">
        <div
          className="relative w-full overflow-hidden rounded-2xl bg-secondary-main bg-cover bg-center [aspect-ratio:1/1]"
          style={{ backgroundImage: `url(${allInIconUrl})` }}
        >
          <CheckBadge selected={allSelected} />
        </div>
        <PricePill amountText={formatTonFromNano(totalNano)} />
      </div>
      <span className="line-clamp-1 w-full text-center text-[14px] font-medium leading-4 tracking-[-0.5px] text-white">
        {label}
      </span>
    </button>
  )
}

function GiftPickerCard({
  gift,
  selected,
  disabled = false,
  onClick,
}: {
  gift: UserGift
  selected: boolean
  disabled?: boolean
  onClick: () => void
}) {
  const name = getDisplayName(gift)
  const price = formatTonFromNano(gift.assessed_price_nano)

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "flex w-full flex-col items-center gap-1 focus:outline-none",
        disabled ? "cursor-not-allowed opacity-60" : "active:scale-[0.97]"
      )}
      aria-pressed={selected}
      aria-label={name}
    >
      <div className="relative w-full pb-2.5">
        <div
          className="relative w-full overflow-hidden rounded-2xl bg-secondary-main bg-cover bg-center [aspect-ratio:1/1]"
          style={{ backgroundImage: `url(${gift.small_image_url})` }}
        >
          <CheckBadge selected={selected} />
        </div>
        <PricePill amountText={price} />
      </div>
      <span className="line-clamp-1 w-full text-center text-[14px] font-medium leading-4 tracking-[-0.5px] text-white">
        {name}
      </span>
    </button>
  )
}

function CheckBadge({ selected }: { selected: boolean }) {
  return (
    <div className="pointer-events-none absolute right-1 top-1 flex h-4 w-4 items-center justify-center">
      {selected ? (
        <div className="flex h-4 w-4 items-center justify-center rounded-full bg-[#00FE74]">
          <Check className="h-2.5 w-2.5 text-white stroke-[3]" />
        </div>
      ) : (
        <div className="h-4 w-4 rounded-full border border-white bg-white/25" />
      )}
    </div>
  )
}

function PricePill({ amountText }: { amountText: string }) {
  return (
    <div className="absolute bottom-0 left-1/2 flex h-5 -translate-x-1/2 items-center gap-0.5 rounded-full bg-white px-1.5">
      <span className="text-[14px] font-medium leading-4 tracking-[-0.5px] text-black-100">
        {amountText}
      </span>
      <TonIcon className="size-3.5 text-black-100" />
    </div>
  )
}
